<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faArrowPointer } from '@fortawesome/free-solid-svg-icons'
    import CanvasBlock from './canvas-block.svelte'
    import DocumentHeader from '../shared/document-header.svelte'
    import DocumentFooter from '../shared/document-footer.svelte'
    import { getEffectiveBlockWidth } from '../blocks/block-registry'
    import type { PlacedBlock } from '../types/placed-block'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'

    export let blocks: PlacedBlock[] = []
    export let headerFooter: DocumentHeaderFooter

    const dispatch = createEventDispatcher()

    let blockEls: HTMLDivElement[] = []
    let dragOverIndex: number | null = null
    let draggingIndex: number | null = null
    let isDraggingOver = false

    function computeDropIndex(event: DragEvent): number {
        const y = event.clientY
        for (let i = 0; i < blocks.length; i++) {
            const el = blockEls[i]
            if (!el) continue
            const rect = el.getBoundingClientRect()
            if (y < rect.top + rect.height / 2) return i
        }
        return blocks.length
    }

    function onDragOver(event: DragEvent) {
        event.preventDefault()
        isDraggingOver = true
        dragOverIndex = computeDropIndex(event)
    }

    function onDragLeave(event: DragEvent) {
        if (event.currentTarget === event.target) {
            isDraggingOver = false
            dragOverIndex = null
        }
    }

    function onDrop(event: DragEvent) {
        event.preventDefault()
        const raw = event.dataTransfer?.getData('application/json')
        const targetIndex = dragOverIndex ?? blocks.length
        isDraggingOver = false
        dragOverIndex = null
        draggingIndex = null
        if (!raw) return

        const payload = JSON.parse(raw)
        if (payload.source === 'palette') {
            dispatch('add', { blockType: payload.blockType, index: targetIndex })
        } else if (payload.source === 'canvas') {
            dispatch('reorder', { from: payload.index, to: targetIndex })
        }
    }

    function onBlockDragStart(event: CustomEvent<{ index: number }>) {
        draggingIndex = event.detail.index
    }

    function onBlockDragEnd() {
        draggingIndex = null
        dragOverIndex = null
        isDraggingOver = false
    }
</script>

<div class="canvas-root pb-6" role="list" on:dragover={onDragOver} on:dragleave={onDragLeave} on:drop={onDrop}>
    <div class="canvas-page">
        <DocumentHeader header={headerFooter.header} />

        {#if blocks.length === 0}
            <div class="empty-state" class:active={isDraggingOver}>
                <Fa icon={faArrowPointer} size="2x" />
                <p>Arraste um bloco da paleta ou clique em um deles para começar</p>
            </div>
        {:else}
            <div class="canvas-grid">
                {#each blocks as block, index (block.id)}
                    {#if isDraggingOver && dragOverIndex === index}
                        <div class="drop-indicator" />
                    {/if}
                    <div class="canvas-grid-item" style="grid-column: span {getEffectiveBlockWidth(block)}" bind:this={blockEls[index]}>
                        <!-- Recria o bloco quando a posição muda. Reordenar faz o {#each} com chave MOVER
                             o nó no DOM, e o navegador recarrega todo <iframe> removido e reinserido. O
                             TinyMCE (texto fixo/rico) monta o documento de edição dentro de um iframe via
                             script: depois do recarregamento sobra um about:blank vazio, sem contentEditable
                             nem listeners — o bloco arrastado aparecia sem conteúdo e não aceitava clique.
                             O texto em si seguia salvo em block.data (o editor só escreve nele em
                             change/input), então recriar restaura tudo. O Svelte aplica os updates dos
                             itens só depois de mover os nós, então o editor novo já nasce na posição final. -->
                        {#key index}
                            <CanvasBlock
                                {block}
                                {index}
                                isDragging={draggingIndex === index}
                                on:dragstart={onBlockDragStart}
                                on:dragend={onBlockDragEnd}
                                on:duplicate={(e) => dispatch('duplicate', e.detail)}
                                on:remove={(e) => dispatch('remove', e.detail)}
                                on:widthchange={(e) => dispatch('widthchange', e.detail)}
                            />
                        {/key}
                    </div>
                {/each}
                {#if isDraggingOver && dragOverIndex === blocks.length}
                    <div class="drop-indicator" />
                {/if}
            </div>
        {/if}

        <DocumentFooter footer={headerFooter.footer} />
    </div>
</div>

<style>
    .canvas-root {
        flex: 1;
        overflow-y: auto;
        padding: 32px 24px;
        background: #1a1a1a;
    }
    .canvas-page {
        max-width: 990px;
        margin: 0 auto;
        min-height: 100%;
        background: #f4f5f7;
        color: #212529;
        border-radius: 16px;
        padding: 24px;
    }
    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        min-height: 320px;
        border: 2px dashed #d0d4da;
        border-radius: 14px;
        color: #adb5bd;
        background: #fff;
        text-align: center;
        padding: 24px;
        transition:
            border-color 0.15s ease,
            background 0.15s ease;
    }
    .empty-state.active {
        border-color: var(--cs2-accent, #4263eb);
        background: #f3f5ff;
        color: var(--cs2-accent, #4263eb);
    }
    .empty-state p {
        margin: 0;
        font-size: 0.9rem;
        max-width: 260px;
    }
    .canvas-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
        align-items: start;
    }
    .canvas-grid-item {
        min-width: 0;
    }
    .drop-indicator {
        grid-column: 1 / -1;
        height: 3px;
        border-radius: 3px;
        background: var(--cs2-accent, #4263eb);
    }

    @media (max-width: 900px) {
        .canvas-root {
            padding: 20px 14px;
        }
        .canvas-grid {
            grid-template-columns: 1fr;
        }
        .canvas-grid-item {
            grid-column: 1 / -1 !important;
        }
    }

    /* ───── tema escuro ─────
       canvas-root (fundo externo) escurece com o tema.
       canvas-page (área do documento) mantém sempre a aparência clara atual.
    */
    :global(.theme_light) .canvas-root {
        background: #f4f5f7;
    }
    :global(.theme_light) .canvas-page {
        /* Força o "papel" do documento a permanecer claro independente do tema */
    }
</style>
